import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';

export interface PlaybackQualityReport {
  /** ms from tracking start (src assigned / slide activated) to first playable frame. */
  startupMs: number | null;
  /** Number of buffering pauses ('waiting' events) during the watch. */
  stalls: number;
  droppedVideoFrames: number;
  totalVideoFrames: number;
  /** Seconds actually watched (currentTime at finalize). */
  watchedSeconds: number;
}

/**
 * Field measurement for the stories player: users report slow loading and A/V desync we
 * cannot reproduce locally, so every watched video reports startup latency, stall count
 * and the browser's dropped-frame counters to GTM. Compare the dashboards before/after
 * playback changes (preload window, HLS rollout).
 *
 * Start when a video becomes active; call the returned finalize() when it stops being
 * active (next swipe / page hide). finalize() detaches listeners and sends ONE event —
 * safe to call multiple times, only the first wins.
 */
export const trackPlaybackQuality = (
  video: HTMLVideoElement,
  meta: { slug: string; source: 'hls' | 'mp4' },
): (() => void) => {
  const startedAt = performance.now();
  let startupMs: number | null = null;
  let stalls = 0;
  let finalized = false;

  const onCanPlay = () => {
    if (startupMs === null) startupMs = Math.round(performance.now() - startedAt);
  };
  const onWaiting = () => {
    stalls += 1;
  };

  video.addEventListener('canplay', onCanPlay);
  video.addEventListener('waiting', onWaiting);

  return () => {
    if (finalized) return;
    finalized = true;
    video.removeEventListener('canplay', onCanPlay);
    video.removeEventListener('waiting', onWaiting);

    const quality = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : undefined;
    const report: PlaybackQualityReport = {
      startupMs,
      stalls,
      droppedVideoFrames: quality?.droppedVideoFrames ?? 0,
      totalVideoFrames: quality?.totalVideoFrames ?? 0,
      watchedSeconds: Math.round(video.currentTime),
    };

    // Skip no-op views (never reached a playable frame AND nothing watched) — swiping
    // straight through a slide would otherwise flood the metric with empty rows.
    if (report.startupMs === null && report.watchedSeconds === 0) return;

    sendGoogleTagEvent('stories_playback_quality', 'Custom', {
      video_slug: meta.slug,
      playback_source: meta.source,
      startup_ms: report.startupMs ?? -1,
      stalls: report.stalls,
      dropped_frames: report.droppedVideoFrames,
      total_frames: report.totalVideoFrames,
      watched_seconds: report.watchedSeconds,
    });
  };
};
